import {Component, EventEmitter, Input, OnChanges, Output} from '@angular/core';
import {CommonModule} from '@angular/common';
import {FormsModule} from '@angular/forms';
import {ContatosService} from '../../../services/contatos.service';
import {
  diasDeAtraso, diasSemPedir, iniciais, moeda, recebeMensagem, segmentoCrm, SegmentoCrm, telefoneFormatado, textoDias
} from '../crm-segmentos';

const DIAS = ['Dom', 'Seg', 'Ter', 'Qua', 'Qui', 'Sex', 'Sáb'];

/**
 * "Mensagem com a Mia": a equipe diz como quer a mensagem (ou escolhe uma ideia pronta), a Mia escreve pelo
 * contexto do cliente e a equipe revisa antes de enviar. Com a loja na V4, sai pela fila do WhatsApp da loja;
 * sem V4, abre o WhatsApp com o texto pronto.
 */
@Component({
  selector: 'app-mensagem-mia-crm',
  standalone: true,
  imports: [CommonModule, FormsModule],
  templateUrl: './mensagem-mia-crm.component.html',
  styleUrls: ['./mensagem-mia-crm.component.scss']
})
export class MensagemMiaCrmComponent implements OnChanges {
  @Input() aberto = false;
  @Input() contato: any = null;
  @Output() fechar = new EventEmitter<void>();

  orientacao = '';
  texto = '';
  escrevendo = false;
  enviando = false;
  erro = '';
  enviado: 'fila' | 'whatsapp' | null = null;
  copiado = false;

  readonly ideias = [
    'Convidar a voltar com carinho',
    'Lembrar do produto favorito',
    'Contar uma novidade do cardápio',
    'Agradecer o último pedido',
    'Pedir uma avaliação no Google'
  ];

  constructor(private contatosService: ContatosService) {}

  ngOnChanges(): void {
    if (this.aberto) {
      this.orientacao = '';
      this.texto = '';
      this.erro = '';
      this.enviado = null;
      this.copiado = false;
    }
  }

  get segmento(): SegmentoCrm {
    return segmentoCrm(this.contato && this.contato.status);
  }

  get iniciais(): string {
    return iniciais(this.contato && this.contato.nome);
  }

  get telefone(): string {
    return telefoneFormatado(this.contato && this.contato.telefone);
  }

  get recebe(): boolean {
    return recebeMensagem(this.contato);
  }

  /** O que a Mia vai levar em conta, em linguagem de gente. */
  get fatos(): Array<{icone: string; texto: string}> {
    const c = this.contato || {};
    const fatos: Array<{icone: string; texto: string}> = [];
    fatos.push({icone: 'fe-clock', texto: textoDias(diasSemPedir(c))});
    if (c.cicloRecompraDias && c.crmUltimoPedidoEm) {
      const atraso = diasDeAtraso(c);
      fatos.push({icone: 'fe-repeat', texto: `pede a cada ${c.cicloRecompraDias} dias` + (atraso ? ` · ${atraso} além` : '')});
    }
    if (Number(c.pedidos90d)) fatos.push({icone: 'fe-shopping-bag', texto: `${c.pedidos90d} pedidos em 90 dias`});
    if (Number(c.ticketMedio90d)) fatos.push({icone: 'fe-tag', texto: `ticket ${moeda(c.ticketMedio90d)}`});
    if (c.crmProdutoFavorito) fatos.push({icone: 'fe-heart', texto: c.crmProdutoFavorito});
    const mascara = Number(c.crmDiasSemanaMask) || 0;
    const dias = DIAS.filter((_, i) => (mascara & (1 << i)) !== 0);
    if (dias.length) fatos.push({icone: 'fe-calendar', texto: 'costuma pedir ' + dias.join(', ')});
    return fatos;
  }

  useIdeia(ideia: string): void {
    this.orientacao = ideia;
  }

  escreva(): void {
    if (!this.contato || !this.contato.id) return;
    this.escrevendo = true;
    this.erro = '';
    this.enviado = null;
    this.contatosService.rascunheMensagemMia(this.contato.id, this.orientacao).then((r: any) => {
      this.texto = r && r.texto ? r.texto : '';
    }).catch((erro: any) => {
      this.erro = typeof erro === 'string' ? erro : 'A Mia não conseguiu escrever agora. Tente de novo.';
    }).finally(() => this.escrevendo = false);
  }

  envie(): void {
    if (!this.texto.trim()) return;
    this.enviando = true;
    this.erro = '';
    this.contatosService.envieMensagemCrm(this.contato.id, this.texto).then((r: any) => {
      if (r && r.pelaFilaV4) {
        this.enviado = 'fila';
      } else if (r && r.linkWhatsapp) {
        window.open(r.linkWhatsapp, '_blank');
        this.enviado = 'whatsapp';
      }
    }).catch((erro: any) => {
      this.erro = typeof erro === 'string' ? erro : 'Não foi possível enviar agora.';
    }).finally(() => this.enviando = false);
  }

  copie(): void {
    const texto = this.texto;
    const concluir = () => { this.copiado = true; setTimeout(() => this.copiado = false, 2000); };
    if (navigator.clipboard && navigator.clipboard.writeText) {
      navigator.clipboard.writeText(texto).then(concluir).catch(() => {});
    }
  }
}
